import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import {
  ShortStream,
  Stream,
} from './types';

export const streamsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getStreams: build.query<CognitoAPIResponse<ShortStream[]>, {
      course?: string
      includeInactive?: boolean
    }>({
      query(arg) {
        const { course, includeInactive } = arg;
        return {
          url: 'streams/query',
          method: 'GET',
          params: {
            course,
            includeInactive,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Streams' as const, id })), 'Streams']
        : ['Streams']),
      transformResponse(response: any) {
        return attachIds(response, (entry: ShortStream) => `${entry.course}#${entry.streamId}`);
      },
    }),
    getStream: build.query<CognitoAPIResponse<Stream>, {
      course: string,
      streamId: string,
    }>({
      query(arg) {
        const { course, streamId } = arg;
        return {
          url: `streams/${course}/${streamId}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [{
        type: 'Streams',
        id: result?.data.id,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry: Stream) => `${entry.course}#${entry.streamId}`);
      },
    }),
    updateStream: build.mutation<any, {
      course: string,
      streamId: string,
      newAttributes: any,
    }>({
      query(arg) {
        const { course, streamId, newAttributes } = arg;

        return {
          url: `streams/${course}/${streamId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Streams', id: `${arg.course}#${arg.streamId}` }],
    }),
    updateStreamPermissionAdd: build.mutation<any, {
      course: string,
      streamId: string,
      permissionId: string,
    }>({
      query(arg) {
        const { course, streamId, permissionId } = arg;

        return {
          url: `streams/${course}/${streamId}/permissions/add`,
          body: { permissionId },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Streams', id: `${arg.course}#${arg.streamId}` }],
    }),
    deleteStreamPermissionRemove: build.mutation<any, {
      course: string,
      streamId: string,
      permissionId: string,
    }>({
      query(arg) {
        const { course, streamId, permissionId } = arg;

        return {
          url: `streams/${course}/${streamId}/permissions/remove`,
          body: { permissionId },
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Streams', id: `${arg.course}#${arg.streamId}` }],
    }),
    deleteStream: build.mutation<any, {
      course: string,
      streamId: string
    }>({
      query(arg) {
        const { course, streamId } = arg;

        return {
          url: `streams/${course}/${streamId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Streams', id: `${arg.course}#${arg.streamId}` },
        { type: 'Courses', id: arg.course },
      ],
    }),
    newStream: build.mutation<any, {
      course: string,
      attributes?: {
        label?: string,
        shortLabel?: string,
        creditsPerTerm?: number,
        terms?: number,
        pricePerLesson?: number,
        importLessons?: boolean,
      }
    }>({
      query(arg) {
        const { course, attributes } = arg;

        return {
          url: 'streams/new',
          body: {
            course,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['Streams'],
    }),
  }),
});

export const {
  useGetStreamsQuery,
  useGetStreamQuery,
  useUpdateStreamMutation,
  useUpdateStreamPermissionAddMutation,
  useDeleteStreamPermissionRemoveMutation,
  useDeleteStreamMutation,
  useNewStreamMutation,
} = streamsApi;
